import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { tx } from '@/translations/translations';
import { Quote, Waypoints } from 'lucide-react';

const SignificadoSection = () => {
  const { language } = useLanguage();

  const teachings = [
    { es: 'La mitzvá de tzitzit equivale a todas las demás mitzvot.', en: 'The mitzvah of tzitzit is equivalent to all other mitzvot.', he: 'שקולה מצוות ציצית כנגד כל המצוות כולן.' },
    { es: 'Mirar el tzitzit conduce al recuerdo, y el recuerdo conduce a la acción.', en: 'Looking at the tzitzit leads to remembrance, and remembrance leads to action.', he: 'ראייה מביאה לידי זכירה, וזכירה מביאה לידי מעשה.' },
    { es: 'Quien es cuidadoso con ella merece recibir la Shejiná.', en: 'Whoever is careful with it deserves to receive the Shejina.', he: 'כל הזהיר במצווה זו זוכה ומקבל פני שכינה.', author: 'Rabi Shimon bar Yojai' },
  ];

  return (
    <div id="significado" className="bg-slate-950 px-4 sm:px-6 pb-16 pt-6">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Pasuk */}
        <div className="relative bg-white/[0.04] border border-white/[0.08] rounded-2xl p-6 sm:p-10 text-center">
          <Quote className="w-8 h-8 text-cyan-500/20 mx-auto mb-4" />
          <p className="text-lg sm:text-xl font-serif italic text-white/90 leading-relaxed mb-4">
            {tx(language, '\"Verán los tzitzit y recordarán todas las mitzvot de Hashem para cumplirlas\"', '\"You shall see the tzitzit and remember all the mitzvot of Hashem to fulfill them\"', '״וראיתם אותו וזכרתם את כל מצוות ה׳ ועשיתם אותם״')}
          </p>
          <div className="flex items-center justify-center gap-3">
            <div className="w-6 h-px bg-cyan-500/30" />
            <p className="text-cyan-400/60 text-xs font-medium tracking-wide">Bamidbar 15:39</p>
            <div className="w-6 h-px bg-cyan-500/30" />
          </div>
        </div>

        {/* Gemara */}
        <p className="text-sm text-slate-400 leading-relaxed">
          {tx(language, 'Sobre este pasuk, la Guemará (Menajot 43b) enseña tres afirmaciones sorprendentes:', 'On this pasuk, the Gemara (Menajot 43b) teaches three surprising statements:', 'על פסוק זה מלמדת הגמרא (מנחות מג ע״ב) שלוש אמירות מפתיעות:')}
        </p>

        {/* 3 Teachings */}
        <div className="space-y-2.5">
          {teachings.map((item, i) => (
            <div key={i} className="flex gap-3 items-start bg-white/[0.04] border border-white/[0.06] rounded-xl p-4">
              <div className="w-7 h-7 rounded-lg bg-cyan-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">{i + 1}</span>
              </div>
              <div>
                <p className="text-white/85 font-medium text-sm leading-relaxed">
                  "{tx(language, item.es, item.en, item.he)}"
                </p>
                {item.author && <p className="text-slate-500 text-xs mt-1">— {item.author}</p>}
              </div>
            </div>
          ))}
        </div>

        {/* Question */}
        <p className="text-sm text-slate-400 leading-relaxed">
          {tx(language, 'Es increíble que el tzitzit equivalga a todas las mitzvot y nos haga merecedores de la Shejiná, pero ¿por qué? y, por último, ¿para qué lo llevamos puesto en todo lugar y en todo momento?', 'It is incredible that the tzitzit is equivalent to all mitzvot and makes us worthy of the Shejina, but why? And finally, why do we wear it everywhere and at all times?', 'מדהים שהציצית שקולה כנגד כל המצוות ומזכה אותנו בקבלת פני השכינה — אבל למה? ולבסוף, לשם מה אנו לובשים אותה בכל מקום ובכל רגע?')}
        </p>

        {/* Answer */}
        <p className="text-sm text-slate-300 leading-relaxed">
          {tx(language, 'El tzitzit encierra un principio poderoso que da respuesta a nuestras preguntas: la persona vive según lo que tiene presente en su mente. No son solo las acciones las que la definen, sino los pensamientos que las originan.', 'The tzitzit contains a powerful principle that answers our questions: a person lives according to what is present in their mind. It is not only actions that define us, but the thoughts that originate them.', 'הציצית טומנת בחובה עיקרון עוצמתי העונה על שאלותינו: האדם חי לפי מה שנמצא במחשבתו. לא רק המעשים מגדירים אותו, אלא המחשבות המולידות אותם.')}
        </p>

        {/* Key quote */}
        <div className="border-l-2 border-cyan-500/40 pl-5">
          <p className="text-white/80 text-base italic leading-relaxed">
            {tx(language, '\"Mirar el tzitzit conduce al recuerdo, y el recuerdo conduce a la acción\"', '\"Looking at the tzitzit leads to remembrance, and remembrance leads to action\"', '״ראייה מביאה לידי זכירה, וזכירה מביאה לידי מעשה״')}
          </p>
        </div>

        <p className="text-sm text-slate-300 leading-relaxed">
          {tx(language, 'El tzitzit actúa en la raíz de nuestras acciones. Así, al llevarlo puesto, nos recuerda constantemente la presencia de Hashem y conduce al cumplimiento de todas las mitzvot, considerándose equivalente a ellas.', 'The tzitzit acts at the root of our actions. Thus, by wearing it, it constantly reminds us of the presence of Hashem and leads to the fulfillment of all mitzvot, being considered equivalent to them.', 'הציצית פועלת בשורש מעשינו. כך, כשאנו לובשים אותה, היא מזכירה לנו תמיד את נוכחות ה׳ ומובילה לקיום כל המצוות — ולכן נחשבת שקולה כנגדן.')}
        </p>

        {/* Closing */}
        <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/5 border border-cyan-400/20 rounded-2xl p-6 sm:p-8 text-center">
          <p className="text-white/90 text-base leading-relaxed font-medium">
            {tx(language, 'Vestir tzitzit todos los días no solo implica el cumplimiento de una mitzvá de la Torá en cada momento; nos encamina al cumplimiento de las mitzvot, al apego a Hashem y al crecimiento espiritual, elevando cada instante de la vida del yehudí.', 'Wearing tzitzit every day does not only imply the fulfillment of a Torah mitzvah at every moment; it guides us toward the fulfillment of mitzvot, attachment to Hashem and spiritual growth, elevating every instant of the life of the yehudi.', 'לבישת ציצית בכל יום אינה רק קיום מצווה מן התורה בכל רגע; היא מכוונת אותנו לקיום המצוות, לדבקות בה׳ ולצמיחה רוחנית, ומרוממת כל רגע בחיי היהודי.')}
          </p>
        </div>

        {/* ─── Meaning of the Knotting ─── */}
        <div id="significado-amarre" className="pt-8 sm:pt-12 border-t border-white/[0.08] scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
              <Waypoints className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white">
              {tx(language, 'Significado del Amarre', 'Meaning of the Knotting', 'משמעות הקשירה')}
            </h3>
          </div>

          {/* Diagrams — images already contain the כנגד י-ה-ו-ה labels */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div className="rounded-2xl overflow-hidden border border-cyan-400/20 bg-black flex flex-col">
              <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-4 py-2.5 text-center">
                <p className="text-[9px] uppercase tracking-[0.2em] text-cyan-100 font-bold">
                  {tx(language, 'Tradición', 'Tradition', 'מסורת')}
                </p>
                <p className="text-lg font-black text-white leading-tight">Sefaradí</p>
                <p className="text-xs text-cyan-100 font-mono mt-0.5">10 · 5 · 6 · 5 = 26</p>
              </div>
              <div className="flex items-center justify-center py-5 px-3">
                <img
                  src="/products/amarre-sefaradi.webp"
                  alt={language === 'es' ? 'Amarre Sefaradí — כנגד י-ה-ו-ה' : 'Sephardic knotting — כנגד י-ה-ו-ה'}
                  className="max-h-[380px] sm:max-h-[440px] w-auto object-contain"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-violet-400/20 bg-black flex flex-col">
              <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-4 py-2.5 text-center">
                <p className="text-[9px] uppercase tracking-[0.2em] text-violet-100 font-bold">
                  {tx(language, 'Tradición', 'Tradition', 'מסורת')}
                </p>
                <p className="text-lg font-black text-white leading-tight">Ashkenazí</p>
                <p className="text-xs text-violet-100 font-mono mt-0.5">7 · 8 · 11 · 13 = 39</p>
              </div>
              <div className="flex items-center justify-center py-5 px-3">
                <img
                  src="/products/amarre-ashkenazi.webp"
                  alt={language === 'es' ? 'Amarre Ashkenazí — כנגד י-ה-ו-ה אחד' : 'Ashkenazi knotting — כנגד י-ה-ו-ה אחד'}
                  className="max-h-[380px] sm:max-h-[440px] w-auto object-contain"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          {/* Paragraphs — professional, concise */}
          <div className="space-y-5">
            <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
              {tx(language, 'El amarre del tzitzit se compone de cuatro grupos de vueltas: 10·5·6·5 en la tradición sefaradí y 7·8·11·13 en la tradición ashkenazí. Ambas son plenamente válidas y cumplen la mitzvá conforme a la halajá.', 'The tzitzit knotting is formed by four groups of windings: 10·5·6·5 in the Sephardic tradition and 7·8·11·13 in the Ashkenazi tradition. Both are fully valid and fulfill the mitzvah according to halakhah.', 'קשירת הציצית מורכבת מארבע קבוצות של כריכות: 10·5·6·5 במסורת הספרדית ו-7·8·11·13 במסורת האשכנזית. שתיהן כשרות לחלוטין ומקיימות את המצווה על פי ההלכה.')}
            </p>

            <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
              {language === 'he' ? (<>
                    בכל קשירה טמונה תזכורת מתמדת לשם ה׳: הסכום הספרדי <span className="text-cyan-300 font-semibold">(26)</span> שווה ל-<span dir="rtl" className="font-serif">י-ה-ו-ה</span>, והסכום האשכנזי <span className="text-violet-300 font-semibold">(39)</span> מוסיף את המילה <span dir="rtl" className="font-serif">אחד</span> — ומזכיר לנו את נוכחותו בכל מקום ובכל רגע שאנו לובשים ציצית.
                  </>)
                : language === 'es'
                ? (<>
                    Cada amarre lleva inscrito un recordatorio permanente del Nombre Divino: la suma sefaradí <span className="text-cyan-300 font-semibold">(26)</span> equivale a <span dir="rtl" className="font-serif">י-ה-ו-ה</span>, y la suma ashkenazí <span className="text-violet-300 font-semibold">(39)</span> añade la palabra <span dir="rtl" className="font-serif">אחד</span> — «Uno» — recordándonos su presencia en todo lugar y momento que vistamos tzitzit.
                  </>)
                : (<>
                    Each knotting carries an enduring reminder of the Divine Name: the Sephardic sum <span className="text-cyan-300 font-semibold">(26)</span> equals <span dir="rtl" className="font-serif">י-ה-ו-ה</span>, while the Ashkenazi sum <span className="text-violet-300 font-semibold">(39)</span> adds the word <span dir="rtl" className="font-serif">אחד</span> — "One" — reminding us of His presence everywhere and every moment we wear tzitzit.
                  </>)}
            </p>

            <div className="relative bg-gradient-to-br from-white/[0.05] to-white/[0.02] border-l-[3px] border-cyan-400/50 rounded-r-xl pl-5 pr-4 py-4">
              <p className="text-sm sm:text-base text-white/90 leading-relaxed italic">
                {tx(language, '«El valor numérico de la palabra tzitzit es 600. Sumados los 8 hilos y los 5 nudos, equivalen y aluden a las 613 mitzvot.»', '\"The numerical value of the word tzitzit is 600. Added to the 8 threads and the 5 knots, they are equivalent to and allude to the 613 mitzvot.\"', '״ציצית בגימטריה 600. בצירוף 8 החוטים ו-5 הקשרים — עולים ורומזים לתרי״ג מצוות.״')}
              </p>
              <div className="flex items-center gap-2 mt-3">
                <div className="w-6 h-px bg-gradient-to-r from-cyan-400/60 to-transparent" />
                <p className="text-slate-400 text-[11px] font-semibold tracking-[0.15em] uppercase">
                  {tx(language, 'Rashí · Bamidbar 15:39', 'Rashi · Bamidbar 15:39', 'רש״י · במדבר טו:לט')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignificadoSection;
